<script setup>
import { computed, onMounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'

import { useGarageStore } from '@/stores/garage.js'
import { useRequestAQuoteStore } from '@/stores/request_a_quote.js'
import { useGeocodeStore } from '@/stores/geocode.js'
import { useAuthenticationStore } from '@/stores/authentication.js'
import { useModalStore } from '@/stores/modal.js'
import { indexUserAddresses } from '@/lib/api/user_addresses.js'

import Map from '@/components/map/map.vue'
import SelectLocation from '@/components/location/select_location.vue'


const props = defineProps({})

// I18n
const { t } = useI18n()

// Stores
const garageStore = useGarageStore()
const geocodeStore = useGeocodeStore()
const requestAQuoteStore = useRequestAQuoteStore()
const authenticationStore = useAuthenticationStore()
const modalStore = useModalStore()

// Variables
const mileLabels = ref([5, 25, 50, 75, 100])
const zoom = ref(12)
const userAddresses = ref([])

// Computed
const primaryAddress = computed(() => 
  userAddresses.value.find(addr => addr.is_primary) || userAddresses.value[0]
)

const hasSelectedLocation = computed(() => 
  requestAQuoteStore.selectedLocation.latitude && requestAQuoteStore.selectedLocation.longitude
)


// Event Handlers
onMounted(async () => {
  await getUserAddresses()
})

watch(() => modalStore.modalVisibility, (isVisible, wasVisible) => {
  if (!isVisible && wasVisible && (modalStore.modalType === modalStore.modalTypeConstants.ADD_ADDRESS || modalStore.modalType === modalStore.modalTypeConstants.EDIT_ADDRESS)) {
    getUserAddresses()
  }
})

// Functions
async function getUserAddresses() {
  if (!authenticationStore.currentUser) return
  
  let params = new URLSearchParams()
  params.set('query[user_id_eq]', authenticationStore.currentUser.user.id)
  params.set('query[archived_eq]', 'false')
  params.set('query[is_ephemeral_eq]', 'false')

  try {
    const response = await indexUserAddresses(params)
    userAddresses.value = response
  } catch (error) {
    console.error('Failed to load user addresses:', error)
  }
}

// Functions

function labelPosition(label) {
  let position = ((label - 5) / 95) * 100

  // Ensure the first and last labels don't overflow
  if (label === 5) {
    position += 2
  } else if (label === 100) {
    position -= 3
  }

  return { left: `${position}%` }
}

</script>

<template>
  <div>
    <div class="mb-5">
      <p class="text-2xl mb-2 font-semibold">
        {{ t('request_a_quote.location_section.title', { vehicle_name: garageStore.currentVehicle?.full_vehicle_name ?? t('request_a_quote.vehicle') }) }}
      </p>
      <p class="text-base">{{ t('request_a_quote.location_section.subtext[0]') }}</p>
      <p class="text-base pt-4">{{ t('request_a_quote.location_section.subtext[1]') }}</p>
    </div>

    <div class="flex flex-col">
      <div v-if="geocodeStore.isError && !requestAQuoteStore.selectedLocation.id && userAddresses.length === 0" class="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-6">
        <div class="flex">
          <div class="flex-shrink-0">
            <svg class="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
              <path fill-rule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clip-rule="evenodd" />
            </svg>
          </div>
          <div class="ml-3">
            <p class="text-sm text-yellow-700">
              {{ t('request_a_quote.location_section.failed_to_retrieve_location') }}
            </p>
            <p class="text-sm text-yellow-700 mt-1">
              {{ t('request_a_quote.location_section.location_permission_help') }}
            </p>
          </div>
        </div>
      </div>

      <div class="flex flex-col lg:flex-row gap-6 mt-6">
        <!-- Location Selection Panel -->
        <div class="lg:w-1/3">
          <div class="border-2 rounded-xl overflow-hidden">
            <div class="p-4 bg-gray-50 border-b">
              <h3 class="text-lg font-semibold">{{ t('request_a_quote.location_section.choose_location') }}</h3>
            </div>
            
            <ul class="divide-y">
              <li v-for="address in userAddresses" :key="address.id"
                  class="p-4 hover:bg-gray-50 transition-colors"
                  :class="{ 'bg-blue-100 border-l-4 border-blue-500': requestAQuoteStore.selectedLocation.id === address.id }">
                <div class="flex justify-between items-center">
                  <div class="flex-1">
                    <span class="font-medium">
                      <span v-if="requestAQuoteStore.selectedLocation.id === address.id" class="text-blue-800">✓</span>
                      🏠 {{ address.nickname || t('request_a_quote.location_section.saved_address') }}
                    </span>
                    <p class="text-sm text-gray-600 mt-1">
                      {{ address.address }}, {{ address.city }}, {{ address.state }}
                    </p>
                  </div>
                  <button 
                    @click="requestAQuoteStore.setLocation(address)"
                    class="ml-4 px-3 py-1 bg-blue-600 text-white text-sm rounded-md hover:bg-blue-700 transition-colors"
                    :class="{ 'bg-green-600 hover:bg-green-700': requestAQuoteStore.selectedLocation.id === address.id }"
                  >
                    {{ requestAQuoteStore.selectedLocation.id === address.id ? t('request_a_quote.location_section.selected') : t('request_a_quote.location_section.use_location') }}
                  </button>
                </div>
              </li>
              
              <li v-if="!geocodeStore.isError" class="p-4 hover:bg-blue-50 transition-colors"
                  :class="{ 'bg-blue-100 border-l-4 border-blue-500': requestAQuoteStore.selectedLocation.latitude === geocodeStore.userLocation.latitude && requestAQuoteStore.selectedLocation.longitude === geocodeStore.userLocation.longitude }">
                <div class="flex justify-between items-center">
                  <div class="flex-1">
                    <span class="font-medium">
                      <span v-if="requestAQuoteStore.selectedLocation.latitude === geocodeStore.userLocation.latitude && requestAQuoteStore.selectedLocation.longitude === geocodeStore.userLocation.longitude" class="text-blue-800">✓</span>
                      🌍 {{ t('request_a_quote.location_section.use_my_location') }}
                    </span>
                    <p class="text-sm text-gray-600 mt-1">
                      {{ geocodeStore.userLocation.latitude ? (geocodeStore.userLocation.address || t('request_a_quote.location_section.detected_location')) : t('request_a_quote.location_section.auto_detect_location') }}
                    </p>
                  </div>
                  <button 
                    @click="geocodeStore.userLocation.latitude ? requestAQuoteStore.setLocation(geocodeStore.userLocation) : geocodeStore.$reset()"
                    class="ml-4 px-3 py-1 bg-blue-600 text-white text-sm rounded-md hover:bg-blue-700 transition-colors"
                    :class="{ 'bg-green-600 hover:bg-green-700': requestAQuoteStore.selectedLocation.latitude === geocodeStore.userLocation.latitude && requestAQuoteStore.selectedLocation.longitude === geocodeStore.userLocation.longitude }"
                  >
                    {{ (requestAQuoteStore.selectedLocation.latitude === geocodeStore.userLocation.latitude && requestAQuoteStore.selectedLocation.longitude === geocodeStore.userLocation.longitude) ? t('request_a_quote.location_section.selected') : t('request_a_quote.location_section.use_location') }}
                  </button>
                </div>
              </li>
              
              <li @click="modalStore.setModal(modalStore.modalTypeConstants.ADD_ADDRESS, { user_id: authenticationStore.currentUser.user.id })" 
                  class="p-4 cursor-pointer hover:bg-gray-50 transition-colors border-t-2 border-dashed border-gray-200">
                <div class="flex items-center justify-center">
                  <span class="font-medium text-blue-600">➕ {{ t('request_a_quote.location_section.add_new_location') }}</span>
                </div>
              </li>
            </ul>
          </div>
        </div>

        <!-- Map -->
        <div class="lg:w-2/3 border rounded-xl overflow-hidden shrink-0 shadow-2xl">
          <div class="h-80 md:h-96 lg:h-[500px]">
            <!-- Map Placeholder -->
            <div v-if="!hasSelectedLocation" class="h-full w-full flex items-center justify-center bg-gray-100">
              <div class="text-center">
                <div class="text-6xl text-gray-400 mb-4">&#128205;</div>
                <p class="text-lg text-gray-600 font-medium">{{ t('request_a_quote.location_section.please_select_location') }}</p>
                <p class="text-sm text-gray-500 mt-2">{{ t('request_a_quote.location_section.map_will_appear_after_selection') }}</p>
              </div>
            </div>
            <!-- Actual Map -->
            <Map v-else
                 :show-user-location="true"
                 :default-zoom="12"
                 :search-location="requestAQuoteStore.selectedLocation"
                 :search-radius="Number(requestAQuoteStore.selectedMileRadius)"
                 class="h-full w-full"></Map>
          </div>
          <p class="text-xs italic text-gray-500 p-2 bg-gray-50">{{ hasSelectedLocation ? t('request_a_quote.location_section.map_legend') : t('request_a_quote.location_section.select_location_to_view_map') }}</p>
        </div>
      </div>
      
      <div class="w-full mt-6 bg-white rounded-xl border p-6 shadow-lg">
        <p class="text-xs text-gray-600 mb-4">
          {{ t('request_a_quote.location_section.quote_disclaimer', { app_name: t('app_name') }) }}
        </p>

        <h3 class="text-lg font-semibold mb-4" style="color: #29476b;">{{ t('request_a_quote.location_section.quote_radius') }}</h3>
        <div class="relative w-full mb-4">
          <input
              type="range"
              min="5"
              max="100"
              v-model="requestAQuoteStore.selectedMileRadius"
              class="slider w-full" />
          <div class="labels">
          <span v-for="label in mileLabels" :key="label" :style="labelPosition(label)" class="text-sm font-medium">
            {{ label }}
          </span>
          </div>
        </div>
      </div>

    </div>
  </div>
</template>

<style lang="scss" scoped>
@import '@/assets/styles/views/request_a_quote/request_a_quote';
</style>